import React, { useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { DsAutocomplete, DsRadioGroup, DsTextField, FullWidth, Title } from "../../../common";
import Button from "../../common/Button";
import { borders, colors, fonts } from "../../common/mixins";
import { Controller, useFormContext } from "react-hook-form";
import type { FieldError } from "react-hook-form";
import type { ReservationFormValues } from "../../../pageComponents/reservationPage/reservation-page.schema";
import { useReservationResources } from "../../../pageComponents/reservationPage/hooks/useReservationResources";
import { useCountriesOptions } from "../../../pageComponents/reservationPage/hooks/useCountriesOptions";
import { apiGet } from "../../../api/fetch";
import { endpoints } from "../../../utils/Constants";
import { Loading } from "../../icons/Loading";
import { BREAKPOINT_RESERVATION_PAGE } from "../../../pageComponents/reservationPage/constants/breakpoints";
import { Section } from "../../../pageComponents/reservation-shared-styles";

const BillingWrap = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  gap: 16px;

  ${borders.lightGrayBorder()};
  border-radius: 8px;
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  width: 100%;

  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    grid-template-columns: 1fr;
  }
`;

const TextWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const SubTitle = styled.h2`
  ${fonts.normalTextBold()};
  color: ${colors.darkBlue};
  line-height: normal;
  margin: 0;
`;

const Text = styled.p`
  ${fonts.normalText()};
  color: ${colors.darkBlue};
  line-height: normal;
  margin: 0;
`;

const InlineTertiaryButton = styled(Button)`
  display: inline;

  div {
    font-size: 16px;
    font-weight: 400;
  }
`;

type GsisCompany = {
  vatNumber: string;
  companyName: string;
  businessActivity: string;
  taxOffice: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
};

type BillingAutoFilledKey =
  | "companyName"
  | "business"
  | "taxOffice"
  | "address"
  | "city"
  | "postalCode"
  | "stateArea"
  | "country";

type BillingField = {
  name: BillingAutoFilledKey;
  label: string;
};

export function Billing() {
  const r = useReservationResources();

  const {
    register,
    control,
    setValue,
    resetField,
    watch,
    getValues,
    trigger,
    clearErrors,
    formState: { errors },
  } = useFormContext<ReservationFormValues>();

  const billingType = watch("billing.type");
  const isGreekCompany = watch("billing.isGreekCompany" as any) !== false;

  const billingFields: BillingField[] = [
    { name: "companyName", label: r.CompanyName },
    { name: "business", label: r.BusinessActivity },
    { name: "taxOffice", label: r.TaxOffice },
    { name: "address", label: r.Address },
    { name: "city", label: r.City },
    { name: "postalCode", label: r.Postcode },
    { name: "country", label: r.Country },
  ];

  const validVat = !!watch("billing.companyName");
  const [vatError, setVatError] = useState<string | null>(null);
  const [vatLoading, setVatLoading] = useState(false);
  const lastValidatedVat = useRef<string | null>(null);

  const countryOptions = useCountriesOptions();
  const foreignCountryOptions = useMemo(
    () => countryOptions.filter((c) => c.Code !== "GR"),
    [countryOptions],
  );
  const billingCountry = watch("billing.country" as any);
  const selectedCountryOption = useMemo(
    () => countryOptions.find((c) => c.Code === billingCountry) ?? null,
    [billingCountry, countryOptions],
  );

  const clearAutoFilledFields = () => {
    billingFields.forEach(({ name }) =>
      setValue(`billing.${name}` as `billing.${BillingAutoFilledKey}`, ""),
    );
    setVatError(null);
    lastValidatedVat.current = null;
  };

  const handleVat = async () => {
    const vat = getValues("billing.vatNumber");
    if (!vat) return;
    if (vat === lastValidatedVat.current) return;

    const isFieldValid = await trigger("billing.vatNumber");
    if (!isFieldValid) return;

    setVatError(null);
    setVatLoading(true);
    lastValidatedVat.current = vat;

    try {
      const result = await apiGet(endpoints.gsis.getCompany(vat));
      const company = result?.data as GsisCompany | null;

      if (company?.vatNumber) {
        setValue("billing.companyName", company.companyName ?? "");
        setValue("billing.business", company.businessActivity ?? "");
        setValue("billing.taxOffice", company.taxOffice ?? "");
        setValue("billing.address", company.address ?? "");
        setValue("billing.city", company.city ?? "");
        setValue("billing.postalCode", company.postalCode ?? "");
        setValue("billing.country", company.country ?? "");
      } else {
        setVatError(r.InvalidVAT);
      }
    } catch {
      setVatError(r.InvalidVAT);
    } finally {
      setVatLoading(false);
    }
  };

  const handleCompanyTypeToggle = (greek: boolean) => {
    resetField("billing.vatNumber");
    clearAutoFilledFields();
    setValue("billing.isGreekCompany" as any, greek);
    clearErrors("billing");
  };

  const billingErrors = errors.billing as
    | Record<string, FieldError | undefined>
    | undefined;

  const showFieldsGrid = validVat || !isGreekCompany;

  return (
    <Section id="billing-section">
      <Title>{r.Billing}</Title>
      <BillingWrap>
        <Controller
          control={control}
          name="billing.type"
          render={({ field }) => (
            <DsRadioGroup
              name="billing-type"
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                resetField("billing.vatNumber");
                clearAutoFilledFields();
                setValue("billing.isGreekCompany" as any, true);
                clearErrors("billing");
              }}
              options={[
                { value: "receipt", label: r.Receipt },
                { value: "invoice", label: r.Invoice },
              ]}
            />
          )}
        />

        {billingType === "invoice" && (
          <>
            <TextWrap>
              <SubTitle>{r.InvoiceDetails}</SubTitle>
              <Text>
                {isGreekCompany
                  ? r.InvoiceDetailsText
                  : r.ForeignCoInvoiceDetailsText}{" "}
                <InlineTertiaryButton
                  variant="tertiary"
                  type="button"
                  text={isGreekCompany ? r.isNotGrCompanyBtn : r.isGrCompanyBtn}
                  onClick={() => handleCompanyTypeToggle(!isGreekCompany)}
                />
              </Text>
            </TextWrap>

            <FullWidth>
              <DsTextField
                key={isGreekCompany ? "vat-greek" : "vat-foreign"}
                label={r.VATNumber}
                placeholder={isGreekCompany ? "123456789" : undefined}
                type="text"
                inputMode={isGreekCompany ? "numeric" : undefined}
                error={!!vatError || !!errors.billing?.vatNumber}
                required
                helperText={vatError ?? errors.billing?.vatNumber?.message}
                InputProps={{
                  endAdornment: vatLoading ? <Loading /> : undefined,
                }}
                {...register("billing.vatNumber", {
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    if (isGreekCompany) {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }
                    if ((validVat || vatError) && isGreekCompany) clearAutoFilledFields();
                    if (errors.billing?.vatNumber) {
                      if (e.target.value) trigger("billing.vatNumber");
                      else clearErrors("billing.vatNumber");
                    }

                    if (isGreekCompany) {
                      if (e.target.value.length === 9) {
                        handleVat();
                      } else if (e.target.value.length > 9) {
                        trigger("billing.vatNumber");
                      }
                    }
                  },
                  onBlur: () => {
                    if (getValues("billing.vatNumber"))
                      trigger("billing.vatNumber");
                  },
                })}
              />
            </FullWidth>

            {showFieldsGrid && (
              <FieldsGrid>
                {billingFields.map((field) =>
                  field.name === "companyName" ? (
                    <FullWidth key={`${field.name}-${isGreekCompany}`}>
                      <DsTextField
                        label={field.label}
                        disabled={isGreekCompany}
                        required={!isGreekCompany}
                        error={!isGreekCompany && !!billingErrors?.[field.name]}
                        helperText={
                          !isGreekCompany
                            ? billingErrors?.[field.name]?.message
                            : undefined
                        }
                        {...register(
                          `billing.${field.name}` as `billing.${BillingAutoFilledKey}`,
                          {
                            onChange: () => {
                              if (billingErrors?.[field.name]) {
                                trigger(
                                  `billing.${field.name}` as `billing.${BillingAutoFilledKey}`,
                                );
                              }
                            },
                          },
                        )}
                      />
                    </FullWidth>
                  ) : field.name === "country" ? (
                    <div key={`${field.name}-${isGreekCompany}`}>
                      <Controller
                        control={control}
                        name="billing.country"
                        render={({ field: rhfField, fieldState }) => {
                          return (
                            <DsAutocomplete
                              autoSelectSingleOption
                              disabled={isGreekCompany}
                              options={isGreekCompany ? countryOptions : foreignCountryOptions}
                              getOptionLabel={(option: { Code: string; Name: string }) =>
                                option.Name
                              }
                              isOptionEqualToValue={(option, value) =>
                                option.Code === value.Code
                              }
                              value={selectedCountryOption}
                              onChange={(
                                _,
                                newValue: { Code: string; Name: string } | null,
                              ) => {
                                rhfField.onChange(newValue?.Code ?? "");
                                if (newValue) trigger("billing.country");
                                else if (fieldState.error) trigger("billing.country");
                              }}
                              onBlur={() => {
                                if (rhfField.value) trigger("billing.country");
                              }}
                              renderInput={(params) => (
                                <DsTextField
                                  {...params}
                                  label={field.label}
                                  required={!isGreekCompany}
                                  error={!isGreekCompany && !!fieldState.error}
                                  helperText={
                                    !isGreekCompany
                                      ? fieldState.error?.message
                                      : undefined
                                  }
                                  inputProps={{
                                    ...params.inputProps,
                                    autoComplete: "new-password",
                                  }}
                                />
                              )}
                            />
                          );
                        }}
                      />
                    </div>
                  ) : (
                    <div key={`${field.name}-${isGreekCompany}`}>
                      <DsTextField
                        label={field.label}
                        disabled={isGreekCompany}
                        required={!isGreekCompany}
                        error={!isGreekCompany && !!billingErrors?.[field.name]}
                        helperText={
                          !isGreekCompany
                            ? billingErrors?.[field.name]?.message
                            : undefined
                        }
                        {...register(
                          `billing.${field.name}` as `billing.${BillingAutoFilledKey}`,
                          {
                            onChange: () => {
                              if (billingErrors?.[field.name]) {
                                trigger(
                                  `billing.${field.name}` as `billing.${BillingAutoFilledKey}`,
                                );
                              }
                            },
                          },
                        )}
                      />
                    </div>
                  ),
                )}
              </FieldsGrid>
            )}
          </>
        )}
      </BillingWrap>
    </Section>
  );
}
